import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { ArrowLeft, Plus, Mail, Trash2 } from 'lucide-react';

const n = v => Number(v) || 0;
const fmtMoney = (v) => { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n(v)); } catch { return `$${n(v).toLocaleString()}`; } };
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const today = () => new Date().toISOString().slice(0, 10);
const inputStyle = { padding: '8px 10px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 13, width: '100%' };

const Stat = ({ label, value, sub, tone, first }) => (
  <div style={{ paddingRight: 26, paddingLeft: first ? 0 : 26, borderLeft: first ? 'none' : '1px solid #26262B' }}>
    <div className="field-label" style={{ marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: tone || '#FAFAFA', fontFamily: '"JetBrains Mono", monospace', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#A1A1AA', marginTop: 5 }}>{sub}</div>}
  </div>
);

const EMPTY_ENTRY = { entry_type: 'charge', amount: '', description: '', entry_date: today(), due_date: '', note: '' };

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [entryModal, setEntryModal] = useState(false);
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY);
  const [entryBusy, setEntryBusy] = useState(false);
  const [entryError, setEntryError] = useState('');

  const [remindModal, setRemindModal] = useState(false);
  const [remindForm, setRemindForm] = useState({ amount_unpaid: '', overdue_balance: '', to_email: '', custom_message: '' });
  const [remindBusy, setRemindBusy] = useState(false);
  const [remindError, setRemindError] = useState('');
  const [remindOk, setRemindOk] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        apiGet(ENDPOINTS.ADMIN.ORG_LEDGER_SUMMARY(id)),
        apiGet(ENDPOINTS.ADMIN.ORG_LEDGER(id)),
      ]);
      setSummary(s);
      setEntries(Array.isArray(e) ? e : []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openEntryModal = () => { setEntryForm(EMPTY_ENTRY); setEntryError(''); setEntryModal(true); };
  const saveEntry = async () => {
    setEntryBusy(true); setEntryError('');
    try {
      const amt = parseFloat(entryForm.amount);
      if (!(amt > 0)) throw new Error('Enter an amount greater than 0.');
      if (!entryForm.description.trim()) throw new Error('Enter a description.');
      await apiPost(ENDPOINTS.ADMIN.ORG_LEDGER(id), {
        entry_type: entryForm.entry_type,
        amount: amt,
        description: entryForm.description.trim(),
        entry_date: entryForm.entry_date,
        due_date: entryForm.entry_type === 'charge' && entryForm.due_date ? entryForm.due_date : null,
        note: entryForm.note.trim() || null,
      });
      setEntryModal(false);
      await load();
    } catch (err) { setEntryError(err.message); }
    finally { setEntryBusy(false); }
  };

  const removeEntry = async (entryId) => {
    if (!window.confirm('Delete this ledger entry? This cannot be undone.')) return;
    try {
      await apiDelete(ENDPOINTS.ADMIN.ORG_LEDGER_ENTRY(id, entryId));
      await load();
    } catch (err) { setError(err.message); }
  };

  const openRemindModal = () => {
    setRemindForm({
      amount_unpaid: summary?.balance != null ? String(summary.balance) : '',
      overdue_balance: summary?.overdue_amount != null ? String(summary.overdue_amount) : '',
      to_email: summary?.contact_email || '',
      custom_message: '',
    });
    setRemindError(''); setRemindOk(''); setRemindModal(true);
  };
  const sendReminder = async () => {
    setRemindBusy(true); setRemindError(''); setRemindOk('');
    try {
      const amountUnpaid = parseFloat(remindForm.amount_unpaid);
      const overdueBalance = parseFloat(remindForm.overdue_balance);
      if (!(amountUnpaid >= 0) || !(overdueBalance >= 0)) throw new Error('Enter valid amounts.');
      const res = await apiPost(ENDPOINTS.ADMIN.ORG_LEDGER_REMIND(id), {
        amount_unpaid: amountUnpaid,
        overdue_balance: overdueBalance,
        to_email: remindForm.to_email.trim() || undefined,
        custom_message: remindForm.custom_message.trim() || undefined,
      });
      setRemindOk(`Reminder sent to ${res.to}.`);
    } catch (err) { setRemindError(err.message); }
    finally { setRemindBusy(false); }
  };

  if (loading) {
    return <div className="page"><div style={{ textAlign: 'center', padding: '60px', color: '#A1A1AA' }}>Loading…</div></div>;
  }
  if (error && !summary) {
    return <div className="page"><div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13 }}>{error}</div></div>;
  }

  const paidOff = n(summary?.balance) <= 0;
  const overdue = n(summary?.overdue_amount) > 0;

  return (
    <div className="page">
      <button onClick={() => navigate('/accounts')} className="flex items-center gap-1.5" style={{ background: 'none', border: 0, color: '#A1A1AA', fontSize: 13, cursor: 'pointer', marginBottom: 14, padding: 0 }}>
        <ArrowLeft size={14} /> Accounts
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 36, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.1 }}>
            {summary?.org_name}
          </h1>
          <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            {summary?.contact_email || 'No contact email on file'}
            {paidOff ? <StatusPill status="paid" /> : overdue ? <StatusPill status="overdue" /> : <StatusPill status="unpaid" />}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={openRemindModal}><Mail size={15} /> Send reminder</Button>
          <Button variant="primary" onClick={openEntryModal}><Plus size={15} /> Add entry</Button>
        </div>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div className="flex flex-wrap items-start" style={{ gap: 0, rowGap: 18, paddingBottom: 20, borderBottom: '1px solid #26262B', marginBottom: 22 }}>
        <Stat first label="Total charged" value={fmtMoney(summary?.total_charged)} />
        <Stat label="Total paid" value={fmtMoney(summary?.total_paid)} tone="#22C55E" />
        <Stat label="Balance" value={fmtMoney(summary?.balance)} tone={paidOff ? '#22C55E' : '#F59E0B'} />
        <Stat label="Overdue" value={fmtMoney(summary?.overdue_amount)} tone={overdue ? '#EF4444' : undefined}
          sub={summary?.oldest_overdue_due_date ? `Oldest due ${fmtDate(summary.oldest_overdue_due_date)}` : undefined} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr><th>Date</th><th>Type</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th><th>Due</th><th>Note</th><th>By</th><th /></tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No entries yet. Add the first charge or payment.</td></tr>}
            {entries.map(e => (
              <tr key={e.id}>
                <td className="tbl-mono">{fmtDate(e.entry_date)}</td>
                <td>
                  <span className={`pill ${e.entry_type === 'charge' ? 'pill-unpaid' : 'pill-paid'}`}>
                    {e.entry_type === 'charge' ? 'Charge' : 'Payment'}
                  </span>
                </td>
                <td style={{ color: '#D4D4D8' }}>{e.description}</td>
                <td className="tbl-mono" style={{ textAlign: 'right', color: e.entry_type === 'charge' ? '#F59E0B' : '#22C55E', fontWeight: 600 }}>
                  {e.entry_type === 'charge' ? '+' : '-'}{fmtMoney(e.amount)}
                </td>
                <td className="tbl-mono" style={{ color: e.due_date && e.due_date < today() ? '#EF4444' : undefined }}>{fmtDate(e.due_date)}</td>
                <td style={{ color: '#A1A1AA', fontSize: 12, maxWidth: 200 }}>{e.note || '-'}</td>
                <td style={{ color: '#A1A1AA', fontSize: 12 }}>{e.created_by_name || '-'}</td>
                <td>
                  <button onClick={() => removeEntry(e.id)} title="Delete entry" style={{ background: 'none', border: 0, color: '#71717A', cursor: 'pointer', padding: 4 }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add entry */}
      <Modal open={entryModal} onClose={() => setEntryModal(false)} title="Add ledger entry" size="md">
        <div className="flex flex-col gap-4">
          {entryError && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13 }}>{entryError}</div>}
          <div className="flex gap-2">
            {['charge', 'payment'].map(t => (
              <button key={t} type="button" onClick={() => setEntryForm(f => ({ ...f, entry_type: t }))}
                style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${entryForm.entry_type === t ? '#3B82F6' : '#34343A'}`,
                  background: entryForm.entry_type === t ? 'rgba(59,130,246,.13)' : '#1E1E22',
                  color: entryForm.entry_type === t ? '#3B82F6' : '#A1A1AA' }}>
                {t === 'charge' ? 'Charge (billed)' : 'Payment (cash received)'}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Amount (USD)</div>
              <input type="number" min="0.01" step="0.01" value={entryForm.amount} onChange={e => setEntryForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} placeholder="0.00" />
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Date</div>
              <input type="date" value={entryForm.entry_date} onChange={e => setEntryForm(f => ({ ...f, entry_date: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Description</div>
            <input value={entryForm.description} onChange={e => setEntryForm(f => ({ ...f, description: e.target.value }))} style={inputStyle}
              placeholder={entryForm.entry_type === 'charge' ? 'e.g. October subscription' : 'e.g. Cash received at office'} />
          </div>
          {entryForm.entry_type === 'charge' && (
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Due date (optional - drives the overdue figure)</div>
              <input type="date" value={entryForm.due_date} onChange={e => setEntryForm(f => ({ ...f, due_date: e.target.value }))} style={{ ...inputStyle, width: 'auto' }} />
            </div>
          )}
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Note (optional)</div>
            <textarea rows={2} value={entryForm.note} onChange={e => setEntryForm(f => ({ ...f, note: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEntryModal(false)} disabled={entryBusy}>Cancel</Button>
            <Button variant="primary" onClick={saveEntry} disabled={entryBusy}>{entryBusy ? 'Saving…' : 'Add entry'}</Button>
          </div>
        </div>
      </Modal>

      {/* Send reminder */}
      <Modal open={remindModal} onClose={() => setRemindModal(false)} title="Send payment reminder" size="md">
        <div className="flex flex-col gap-4">
          {remindError && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13 }}>{remindError}</div>}
          {remindOk && <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 8, color: '#22C55E', fontSize: 13 }}>{remindOk}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Amount unpaid</div>
              <input type="number" min="0" step="0.01" value={remindForm.amount_unpaid} onChange={e => setRemindForm(f => ({ ...f, amount_unpaid: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Overdue balance</div>
              <input type="number" min="0" step="0.01" value={remindForm.overdue_balance} onChange={e => setRemindForm(f => ({ ...f, overdue_balance: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Send to</div>
            <input type="email" value={remindForm.to_email} onChange={e => setRemindForm(f => ({ ...f, to_email: e.target.value }))} style={inputStyle} placeholder="billing@organization.com" />
          </div>
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Additional message (optional)</div>
            <textarea rows={3} value={remindForm.custom_message} onChange={e => setRemindForm(f => ({ ...f, custom_message: e.target.value }))} style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Anything you want to add to the standard reminder email." />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRemindModal(false)} disabled={remindBusy}>Close</Button>
            <Button variant="primary" onClick={sendReminder} disabled={remindBusy || !remindForm.to_email.trim()}>{remindBusy ? 'Sending…' : 'Send email'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
